// src/app/AppHeader.tsx
/**
 * The top bar of the authenticated shell: offline banner trigger area, mobile menu button,
 * clock status, active-tab title, chat/notification bells, theme toggle and help actions.
 *
 * Extracted from AuthedApp.tsx (plans/monolith-migration-plan.md §11.12, Batch 21).
 */
import type { TFunction } from 'i18next';
import { Bell, Building2, Link2, Menu, MessageSquare, Moon, Sun, Ticket } from 'lucide-react';
import { HandoverGlobalBanner } from '../features/handover/HandoverGlobalBanner';
import InstallPwaButton from '../components/InstallPwaButton';
import { TAB_TITLES, showToast } from '../constants';
import type { TenantConfig } from '../AuthedApp';

export interface AppHeaderProps {
    t: TFunction;
    activeTab: string;
    user: { name?: string; firstName?: string };
    allowedTabs: string[];
    tenantConfig: TenantConfig | null;
    formattedDate: string;
    clockStatus: string;
    clockKey: string;
    clockLabels: Record<string, string>;
    clockColors: Record<string, string>;
    badgeBg: Record<string, string>;
    displayMs: number;
    formatClockTime: (ms: number) => string;
    unreadCount: number;
    chatUnreadCount: number;
    requestPushPermission: () => Promise<unknown> | void;
    getRoleData: (role: string | null | undefined) => { name: string; badge: string; tabs: string[] } | null;
    userRole: string | null;
    hasUnreadUserReplies: boolean;
    theme: string;
    toggleTheme: () => void;
    setIsMobileMenuOpen: (v: boolean) => void;
    setIsPreferencesOpen: (v: boolean) => void;
    setIsSupportOpen: (v: boolean) => void;
    setIsHistoryOpen: (v: boolean) => void;
    handleNavClick: (tab: string) => void;
}

export function AppHeader({
    t,
    activeTab,
    user,
    allowedTabs,
    tenantConfig,
    formattedDate,
    clockStatus,
    clockKey,
    clockLabels,
    clockColors,
    badgeBg,
    displayMs,
    formatClockTime,
    unreadCount,
    chatUnreadCount,
    requestPushPermission,
    getRoleData,
    userRole,
    hasUnreadUserReplies,
    theme,
    toggleTheme,
    setIsMobileMenuOpen,
    setIsPreferencesOpen,
    setIsSupportOpen,
    setIsHistoryOpen,
    handleNavClick,
}: AppHeaderProps) {
    const getTabHelpText = (tab: string): { text: string; hasDot?: boolean } | null => {
        switch (tab) {
            case 'analytics':
                return { text: t('analytics.dataSource'), hasDot: true };
            case 'hk':
                return { text: t('housekeeping.descriptionPlan'), hasDot: true };
            case 'groups':
                return { text: t('groupsList.subtitle'), hasDot: true };
            case 'traces':
                return { text: t('traces.description'), hasDot: true };
            case 'fundsachen':
                return { text: t('fundsachen.description'), hasDot: true };
            case 'sales':
                return { text: t('sales.viewSubtitle'), hasDot: true };
            case 'anleitungen':
                return { text: t('anleitungen.subtitle'), hasDot: true };
            case 'inventory':
                return { text: t('inventory.subtitle'), hasDot: true };
            case 'automations':
                return { text: t('automations.subtitle'), hasDot: true };
            case 'workflows':
                return { text: t('automationBuilder.workflowsSubtitle', 'Deine eigenen Wenn-dann-Regeln'), hasDot: true };
            case 'xrechnung':
                return { text: t('xrechnung.subtitle'), hasDot: true };
            case 'buchhaltung':
                return { text: t('buchhaltung.subtitle'), hasDot: true };
            case 'availability':
                return { text: t('availability.subtitle'), hasDot: true };
            case 'roomrack':
                return { text: t('roomrack.subtitle', 'Zimmerbelegung & Buchungen verwalten'), hasDot: true };
            case 'hr':
                return { text: t('hr.clock.description', 'Arbeitszeit, Abwesenheiten & Zeiterfassung'), hasDot: true };
            case 'ht':
                return { text: t('internalResources.subtitle', 'Zimmerstatus, Reparaturen & Wartungsfenster'), hasDot: true };
            case 'internalresources':
                return { text: t('internalResources.subtitle'), hasDot: true };
            case 'offers':
                return { text: t('offersView.subtitle', 'Angebotsverwaltung & Gästebestätigungen'), hasDot: true };
            default:
                return null;
        }
    };

    const tabHelp = getTabHelpText(activeTab);

    return (
    <header className="bg-white dark:bg-surface border-b border-slate-200 dark:border-border p-3 sm:p-4 flex justify-between items-center shrink-0 z-45 shadow-xs px-4 sm:px-6 print:hidden">
        <div className="flex items-center gap-1.5 sm:gap-3">
            <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden p-1.5 -ml-1.5 text-slate-900 dark:text-slate-200 hover:bg-[#f3f5ef] dark:hover:bg-slate-800 hover:text-[#859F4A] rounded-lg transition-colors"
            >
                <Menu size={20} />
            </button>
            {/* Schichtübergabe: compact pill/icon when a handover waits for acknowledgement */}
            <HandoverGlobalBanner user={user} />

            <div className="flex flex-col select-none">
                <h1 className="text-base sm:text-lg font-bold text-black dark:text-foreground tracking-tight flex items-center gap-1.5 leading-tight">
                    <Building2 size={16} className="text-[#859f4a] shrink-0 md:hidden" />
                    <span>
                        {allowedTabs.includes(activeTab)
                            ? (activeTab === "iframe" && tenantConfig?.customIframeName
                                ? tenantConfig.customIframeName
                                : t("tabs." + activeTab, TAB_TITLES[activeTab as keyof typeof TAB_TITLES] || "Adapt Hub"))
                            : "Adapt Hub"}
                    </span>
                </h1>
                {tabHelp && (
                    <div className="flex items-center gap-1.5 mt-0.5">
                        {tabHelp.hasDot && (
                            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#859F4A] animate-pulse shrink-0" />
                        )}
                        <p className="text-[9px] sm:text-xs font-bold sm:font-medium text-[#6B7B90] dark:text-slate-400 uppercase sm:normal-case tracking-[0.12em] sm:tracking-normal leading-tight">
                            {tabHelp.text}
                        </p>
                    </div>
                )}
            </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-4 shrink-0">
            {/* Date and Clock Status (ON THE LEFT) */}
            <div className="hidden lg:flex items-center gap-3.5 pr-1">
                <span className="text-xs sm:text-sm font-semibold text-[#535C68] dark:text-muted uppercase tracking-wider">{formattedDate}</span>
                {clockStatus === 'out' ? (
                    <span className="text-xs font-medium text-[#6B7B90] dark:text-muted">{t('hr.clock.statusOut')}</span>
                ) : (
                    <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-0.5 rounded text-[10px] font-bold ${badgeBg[clockKey]} ${clockColors[clockKey]}`}>
                            {clockLabels[clockKey]}
                        </span>
                        <span className={`text-xs sm:text-sm font-semibold ${clockColors[clockKey]}`}>
                            {formatClockTime(displayMs)}
                        </span>
                    </div>
                )}
            </div>

            {/* Divider between Date and Quick Buttons (desktop only) */}
            <div className="hidden lg:block h-6 w-px bg-slate-200/80 dark:bg-slate-800 shrink-0" />

            {/* Quick Buttons (ON THE RIGHT) */}
            <div className="shrink-0">
                <InstallPwaButton />
            </div>

            {/* Theme Toggle Button (Dark / Light Mode) */}
            <button
                type="button"
                onClick={toggleTheme}
                className="flex relative p-1 bg-transparent hover:text-[#859F4A] dark:hover:text-[#859F4A] transition-colors shrink-0 text-slate-600 dark:text-slate-300 active:scale-[0.95] items-center justify-center cursor-pointer"
                title={theme === 'dark' ? t('common.lightMode', 'Heller Modus') : t('common.darkMode', 'Dunkler Modus')}
                aria-label={theme === 'dark' ? t('common.lightMode', 'Heller Modus') : t('common.darkMode', 'Dunkler Modus')}
            >
                {theme === 'dark' ? (
                    <Sun className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" strokeWidth={2} />
                ) : (
                    <Moon className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" strokeWidth={2} />
                )}
            </button>

            <button
                onClick={() => setIsSupportOpen(true)}
                className="relative p-1 bg-transparent hover:text-[#859F4A] dark:hover:text-[#859F4A] transition-colors shrink-0 text-slate-600 dark:text-slate-300 active:scale-[0.95]"
                title="Support & Hilfe"
            >
                <Ticket className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} />
                {hasUnreadUserReplies && (
                    <span className="absolute top-0 right-0 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-red-500 rounded-full ring-2 ring-white dark:ring-slate-900 shadow-xs" />
                )}
            </button>

            <button
                onClick={() => {
                    const url = window.location.href;
                    navigator.clipboard.writeText(url).then(() => {
                        showToast(t("offersView.linkCopied", "Link kopiert!"), "success");
                    }).catch(() => {
                        showToast(url, "info");
                    });
                }}
                className="flex relative p-1 bg-transparent hover:text-[#859F4A] dark:hover:text-[#859F4A] transition-colors shrink-0 text-slate-600 dark:text-slate-300 active:scale-[0.95] items-center justify-center"
                title={t("offersView.copyLink", "Link kopieren")}
            >
                <Link2 className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} />
            </button>

            {allowedTabs.includes("chat") && (
                <button
                    type="button"
                    onClick={() => handleNavClick("chat")}
                    className={`flex relative p-1 bg-transparent hover:text-[#859F4A] dark:hover:text-[#859F4A] transition-colors shrink-0 ${
                        activeTab === "chat"
                            ? "text-[#859F4A]"
                            : "text-slate-600 dark:text-slate-300"
                    } active:scale-[0.95] items-center justify-center cursor-pointer`}
                    title={t("tabs.chat", "Chat")}
                    aria-label={t("tabs.chat", "Chat")}
                >
                    <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} />
                    {chatUnreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-white dark:ring-slate-900 shadow-xs animate-in fade-in">
                            {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                        </span>
                    )}
                </button>
            )}

            <button
                onClick={() => {
                    setIsHistoryOpen(true);
                    // User gesture — the only reliable moment to ask
                    // for notification permission on iOS.
                    requestPushPermission();
                }}
                className="flex relative p-1 bg-transparent hover:text-[#859F4A] dark:hover:text-[#859F4A] transition-colors shrink-0 text-slate-600 dark:text-slate-300 active:scale-[0.95] items-center justify-center"
                title="Notifications"
            >
                <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-white dark:ring-slate-900 shadow-xs">
                        {unreadCount}
                    </span>
                )}
            </button>

            <div
                className="hidden sm:flex items-center gap-3 pl-3 sm:pl-4 border-l border-slate-200/80 dark:border-slate-800 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setIsPreferencesOpen(true)}
            >
                <div className="text-right">
                    <p className="text-xs font-bold text-black dark:text-foreground leading-none">
                        {user?.name}
                    </p>
                    <p className="text-[10px] text-[#859f4a] font-medium uppercase mt-1">
                        {getRoleData(userRole)?.name}
                    </p>
                </div>
                <div className="w-10 h-10 bg-[#859F4A] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-inner uppercase shrink-0">
                    {user?.name ? user.name.substring(0, 2) : "?"}
                </div>
            </div>
        </div>
    </header>
    );
}
